import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Header, Zone } from '@aptsi-types';
import { MatDialog } from '@angular/material/dialog';
import { ZoneService } from '../../services/zone.service';
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { DeleteDialogComponent } from 'src/app/shared/dialogs/delete-dialog/delete-dialog.component';
import { ZoneAssetsComponent } from '../zone-assets/zone-assets.component';
import { WorkflowNavigationService } from '../../../shared/workflow-navigation.service';

type ZoneWithPagination = Zone & {
  totalZones?: number;
  totalItems?: number;
  totalCount?: number;
  totalRecords?: number;
  totalPages?: number;
  currentPage?: number;
};

@Component({
  selector: 'app-zone-search',
  templateUrl: './zone-search.component.html',
  styleUrls: ['./zone-search.component.scss']
})
export class ZoneSearchComponent implements OnInit {
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  readonly itemsPerPage = 7;
  currentPage = 1;
  isAdd = false;
  isSearch = false;
  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;

  readonly sessionId: string;
  readonly userId: string;
  readonly transactionId: string;

  filteredData: Zone[] = [];
  searchedData: Zone[] = [];

  get displayedData(): Zone[] {
    return this.isSearch ? this.searchedData : this.filteredData;
  }

  readonly headers: Header[] = [
    {
      name: 'Zone ID',
      property: 'id',
      showInDropdown: true,
      description: 'Unique identifier for the zone'
    },
    {
      name: 'Zone Name',
      property: 'zoneName',
      showInDropdown: true,
      description: 'Name of the zone'
    },
    {
      name: 'Description',
      property: 'description',
      showInDropdown: false,
      description: 'Description of the zone'
    },
    {
      name: 'Last Updated',
      property: 'updatedAt',
      showInDropdown: true,
      description: 'Timestamp of the most recent update'
    }
  ];

  searchText = '';
  filterByHeader = 'all';
  nullver = 1;

  readonly buttonConfig = {
    update: true,
    delete: true,
    import: false,
    showAssets: true
  };

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly zoneService: ZoneService,
    private readonly dialog: MatDialog,
    keycloakStateService: KeycloakStateService,
    private readonly workflowNavigation: WorkflowNavigationService,
  ) {
    this.sessionId = keycloakStateService.getSessionId();
    this.userId = keycloakStateService.getUserId();
    this.transactionId = keycloakStateService.getTransactionId();
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.isAdd = params['isAdd'] === 'true';
    });

    void this.fetchZones();
  }

  handleAction(event: { action: string; rowIndex: number; rowData: Zone }): void {
    switch (event.action) {
      case 'update':
        this.updateZone(event.rowIndex);
        break;
      case 'delete':
        void this.deleteZone(event.rowIndex);
        break;
      case 'showAssets':
        this.showZoneAssets(event.rowIndex);
        break;
      default:
        break;
    }
  }

  async fetchZones(): Promise<void> {
    this.isLoading = true;
    this.loadError = false;

    try {
      const apiResponse = await this.zoneService
        .getZones(this.currentPage - 1)
        .toPromise();
      const response: Zone[] = Array.isArray(apiResponse) ? apiResponse : [];
      const metadata = response[0] as ZoneWithPagination | undefined;

      this.filteredData = [...response];
      this.totalPagesNormal = this.readNonNegativeNumber(
        metadata?.totalPages,
        response.length > 0 ? 1 : 0
      );
      this.totalItemsNormal = this.readNonNegativeNumber(
        metadata?.totalZones ?? metadata?.totalItems ?? metadata?.totalCount ?? metadata?.totalRecords,
        response.length
      );
      this.totalPages = this.totalPagesNormal;

      if (typeof metadata?.currentPage === 'number' && metadata.currentPage >= 0) {
        this.currentPage = metadata.currentPage + 1;
      } else if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      }
    } catch (error) {
      console.error('Error fetching zones:', error);
      this.loadError = true;
      this.filteredData = [];
      this.totalPagesNormal = 0;
      this.totalItemsNormal = 0;
      this.totalPages = 0;
    } finally {
      this.isLoading = false;
    }
  }

  async fetchZonesSearch(): Promise<void> {
    this.isSearching = true;
    this.searchError = false;

    try {
      const apiResponse = await this.zoneService
        .getZonesSearch(
          this.currentPage - 1,
          this.searchText.trim(),
          this.filterByHeader
        )
        .toPromise();
      const response: Zone[] = Array.isArray(apiResponse) ? apiResponse : [];
      const metadata = response[0] as ZoneWithPagination | undefined;

      this.searchedData = [...response];
      this.totalPagesSearch = this.readNonNegativeNumber(
        metadata?.totalPages,
        response.length > 0 ? 1 : 0
      );
      this.totalItemsSearch = this.readNonNegativeNumber(
        metadata?.totalZones ?? metadata?.totalItems ?? metadata?.totalCount ?? metadata?.totalRecords,
        response.length
      );
      this.totalPages = this.totalPagesSearch;

      if (typeof metadata?.currentPage === 'number' && metadata.currentPage >= 0) {
        this.currentPage = metadata.currentPage + 1;
      }
    } catch (error) {
      console.error('Error searching zones:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalPagesSearch = 0;
      this.totalItemsSearch = 0;
      this.totalPages = 0;
    } finally {
      this.isSearching = false;
    }
  }

  async searchZones(): Promise<void> {
    const query = this.searchText.trim();

    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.isSearch = true;
    this.currentPage = 1;
    await this.fetchZonesSearch();

    if (this.searchError) {
      return;
    }

    // Preserve the existing client-side filtering behavior after the API request.
    const selectedHeader = this.headers.find(
      header => header.property === this.filterByHeader && header.showInDropdown
    );

    if (this.filterByHeader === 'all') {
      this.searchedData = this.searchedData.filter(zone =>
        this.headers.some(header => {
          const value = (zone as any)[header.property];
          return value?.toString().toLowerCase().includes(this.searchText.toLowerCase());
        })
      );
    } else if (selectedHeader) {
      this.searchedData = this.searchedData.filter(zone => {
        const value = (zone as any)[selectedHeader.property];
        return value?.toString().toLowerCase().includes(this.searchText.toLowerCase());
      });
      this.searchedData.sort((left, right) => {
        const leftValue = (left as any)[selectedHeader.property]?.toString() ?? '';
        const rightValue = (right as any)[selectedHeader.property]?.toString() ?? '';
        return leftValue.localeCompare(rightValue);
      });
    }
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    this.currentPage = 1;
    if (this.searchText.trim()) {
      this.isSearch = true;
      void this.searchZones();
    } else {
      void this.clearSearch();
    }
  }

  async clearSearch(): Promise<void> {
    this.searchText = '';
    this.isSearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.totalPages = this.totalPagesNormal;
    await this.fetchZones();
  }

  resetSearchText(): void {
    this.searchText = '';
    this.isSearch = false;
    this.currentPage = 1;
  }

  changePage(page: number): void {
    if (!Number.isFinite(page) || page < 1 || page === this.currentPage) {
      return;
    }

    this.currentPage = page;
    if (this.isSearch) {
      void this.fetchZonesSearch();
    } else {
      void this.fetchZones();
    }
  }

  addZone(): void {
    void this.workflowNavigation.navigateToWorkflow(
      'securityZoneManagement',
      ['zone', 'create']
    );
  }

  showZoneAssets(index: number): void {
    const zone = this.displayedData[index];
    const zoneId = zone?.id;

    if (zoneId === null || zoneId === undefined || zoneId === '') {
      console.error('Cannot show zone assets: zone ID is missing.', zone);
      return;
    }

    this.dialog.open(ZoneAssetsComponent, {
      data: { zoneId },
      width: '1000px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false
    });
  }

  async deleteZone(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Are you sure?',
          message: 'Do you really want to delete this Zone?',
          isConfirm: false
        }
      });

      const confirmed = await dialogRef.afterClosed().toPromise();
      if (!confirmed) return;

      const zoneId = this.displayedData[index]?.id;
      if (zoneId === null || zoneId === undefined || zoneId === '') return;

      await this.zoneService.deleteZone(zoneId).toPromise();

      if (this.isSearch) {
        await this.fetchZonesSearch();
      } else {
        await this.fetchZones();
      }

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: 'Success',
          message: 'The Zone has been deleted successfully.',
          isConfirm: true
        }
      });
    } catch (error) {
      console.error('Error deleting zone:', error);
    }
  }

  updateZone(index: number): void {
  const zoneId = this.displayedData[index]?.id;

  if (zoneId === null || zoneId === undefined || zoneId === '') {
    return;
  }

    void this.workflowNavigation.navigateToWorkflow(
      'securityZoneManagement',
      ['zone', 'update', String(zoneId)]
    );
  }

  back(): void {
    if (this.isSearch) {
      void this.clearSearch();
      return;
    } else {
      void this.workflowNavigation.navigateToProductHome();
    }
  }

  private readNonNegativeNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }
}
